import { Table, TableBody, TableContainer } from "@mui/material"
import { useEffect } from "react"
import { Bound } from "lib/@components/binding/Bound"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { NoResults } from "lib/@components/no-results/no-results"
import { pick } from "lib/pick"
import { compareValues } from "lib/compareValues"
import { DataTableHeader } from "./data-table-header"
import { DataTablePagination } from "./data-table-pagination"
import { useTableQuery } from "./use-table-query"

/**NOTE: Row filtering happens in the caller before `items` is passed in — DataTable never filters.
 *
 * If `items` present => client mode: sort + paginate the whole list in memory here.
 *
 * If `items` absent => server mode: the caller passes the current-page `rows` + overall `total`
 * and re-fetches each page via `onQueryChange`, so sorting/paging happen server-side, not here.
 * onQueryChange could be passed like: { skip: 0, take: 10, sort: { field: "name", descending: false } }*/

export function DataTable({
    columns,
    RowComponent,
    items,
    rows,
    total,
    onQueryChange,
    sortKey,
    pageSizeKey,
    defaultPageSize = 10,
    defaultSort = null,
    refreshKey,
    itemLabel = "results",
    noResults = <NoResults />,
    isFiltered = false,
    keyFn,
    bind,
    sx,
}) {
    const { skip, take, sort, page, setTake, setPage, toggleSort } = useTableQuery({
        sortKey,
        pageSizeKey,
        defaultPageSize,
        defaultSort,
        refreshKey,
    })

    const isClientMode = items !== undefined
    const totalCount = isClientMode ? items.length : total ?? 0
    const visibleRows = isClientMode ? orderClientItems().slice(skip, skip + take) : rows ?? []
    const hasRows = totalCount > 0

    useEffect(notifyServerQuery, [skip, take, sort, isClientMode, onQueryChange])
    useEffect(goToLastPage, [totalCount, take, skip, setPage])

    const pager = (
        <DataTablePagination
            skip={skip}
            take={take}
            total={totalCount}
            page={page}
            setTake={setTake}
            setPage={setPage}
            itemLabel={itemLabel}
        />
    )

    return (
        <>
            <If value={hasRows}>{pager}</If>
            <If value={hasRows || isFiltered}>
                <Bound sort={sort} toggleSort={toggleSort} defaultSort={defaultSort}>
                    <TableContainer>
                        <Table size="small" sx={{ tableLayout: "fixed", width: "100%", ...sx }}>
                            <DataTableHeader columns={columns} />
                            <TableBody>
                                <Repeat list={visibleRows} item={RowComponent} keyFn={keyFn} bind={bind} />
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Bound>
            </If>
            <If value={hasRows}>{pager}</If>
            <If not value={hasRows}>
                {noResults}
            </If>
        </>
    )

    function orderClientItems() {
        if (!sort?.field) return items
        const getValue = pick(sort.field)
        const sorted = [...items].sort(compareByField)
        return sort.descending ? sorted.reverse() : sorted

        function compareByField(a, b) {
            return compareValues(getValue(a), getValue(b))
        }
    }

    function notifyServerQuery() {
        // Server mode only: when the page, page size or sort changes, ask the caller to fetch that slice.
        if (!isClientMode) onQueryChange?.({ skip, take, sort })
    }

    function goToLastPage() {
        if (totalCount > 0 && skip >= totalCount) {
            setPage(Math.ceil(totalCount / take))
        }
    }
}
